import { useState, useMemo } from 'react'
import { useQuery, useMutation, useQueryClient } from 'react-query'
import { imagesAPI, categoriesAPI, sourcesAPI, catalogAPI } from '../api/client'
import { Star, TrendingUp, Minus, TrendingDown, ExternalLink, X, RefreshCw, BarChart3, Eye, Download, Heart, Share2, FileText, CheckCircle, Check, HardDrive, Globe, Tag, Filter, Trash2, Undo2, Search, Calendar, Upload } from 'lucide-react'
import { useToast } from '../components/Toast'

// Tier config
const TIERS = {
  masterpiece: {
    label: 'Шедевры',
    range: '90-100%',
    icon: Star,
    color: 'text-yellow-400',
    bgColor: 'bg-yellow-500/20',
    description: 'Топ 10% — лучшие открытки'
  },
  good: {
    label: 'Хорошие',
    range: '75-90%',
    icon: TrendingUp,
    color: 'text-green-400',
    bgColor: 'bg-green-500/20',
    description: '75-90% — выше среднего'
  },
  average: {
    label: 'Средние',
    range: '50-75%',
    icon: Minus,
    color: 'text-blue-400',
    bgColor: 'bg-blue-500/20',
    description: '50-75% — требуют ручной проверки'
  },
  weak: {
    label: 'Слабые',
    range: '<50%',
    icon: TrendingDown,
    color: 'text-red-400',
    bgColor: 'bg-red-500/20',
    description: 'Нижние 50% — кандидаты на удаление'
  }
}

const PUBLISH_TARGET_FILTERS = [
  { key: 'new', label: 'Новые', color: 'text-green-400' },
  { key: 'agr', label: 'AGR', color: 'text-blue-300' },
  { key: 'site', label: 'SITE', color: 'text-orange-300' },
  { key: 'both', label: 'AGR + SITE', color: 'text-purple-300' },
  { key: 'site_link', label: 'Со ссылкой сайта', color: 'text-cyan-300' },
  { key: 'published', label: 'Любая публикация', color: 'text-cyan-300' },
  { key: 'all', label: 'Все', color: 'text-white' },
]

function getPublishedTargets(image) {
  const targets = new Set(Array.isArray(image?.published_to) ? image.published_to : [])
  if (image?.agr_id) targets.add('agr')
  if (image?.lojs_id) targets.add('lojs')
  return targets
}

function isPublishedToAgr(image) {
  return getPublishedTargets(image).has('agr')
}

function isPublishedToSite(image) {
  return getPublishedTargets(image).has('lojs')
}

function getPublishBadges(image) {
  const hasAgr = isPublishedToAgr(image)
  const hasSite = isPublishedToSite(image)

  if (!hasAgr && !hasSite) return []

  const badges = []
  if (hasAgr) {
    badges.push({ key: 'agr', label: 'AGR', className: 'bg-blue-500/80' })
  }
  if (hasSite) {
    badges.push({ key: 'lojs', label: 'SITE', className: 'bg-orange-500/80' })
  }
  return badges
}

function getPublishStateSummary(image) {
  const hasAgr = isPublishedToAgr(image)
  const hasSite = isPublishedToSite(image)

  if (hasAgr && hasSite) return { label: 'AGR + сайт', className: 'text-purple-300' }
  if (hasAgr) return { label: 'Только AGR', className: 'text-blue-300' }
  if (hasSite) return { label: 'Только сайт', className: 'text-orange-300' }
  return { label: 'Новая', className: 'text-green-300' }
}

function getSiteSeoState(image) {
  const status = String(image?.lojs_seo_status || '').trim().toLowerCase()
  const hasSitePage = Boolean(image?.lojs_id || image?.lojs_public_url)

  if (!hasSitePage) return null
  if (status === 'done') return { label: 'SEO готово', className: 'text-emerald-300', badgeClassName: 'bg-emerald-500/80' }
  if (status === 'failed' || status === 'error') return { label: 'SEO ошибка', className: 'text-red-300', badgeClassName: 'bg-red-500/80' }
  if (status === 'pending' || status === 'leased' || status === 'queued' || status === 'processing') {
    return { label: 'SEO в работе', className: 'text-amber-300', badgeClassName: 'bg-amber-500/80' }
  }
  return { label: 'SEO неизвестно', className: 'text-slate-300', badgeClassName: 'bg-slate-500/80' }
}

function matchesPublishTargetFilter(image, filter) {
  const hasAgr = isPublishedToAgr(image)
  const hasSite = isPublishedToSite(image)

  switch (filter) {
    case 'new':
      return !hasAgr && !hasSite
    case 'agr':
      return hasAgr && !hasSite
    case 'site':
      return hasSite && !hasAgr
    case 'both':
      return hasAgr && hasSite
    case 'site_link':
      return Boolean(image?.lojs_public_url)
    case 'published':
      return hasAgr || hasSite
    case 'all':
    default:
      return true
  }
}

function filterImagesByPublishTarget(images, filter) {
  if (!Array.isArray(images)) return []
  return images.filter(image => matchesPublishTargetFilter(image, filter))
}

function countByPublishTarget(images, filter) {
  return filterImagesByPublishTarget(images, filter).length
}

function StatCard({ icon: Icon, label, value, subValue, color = 'text-white' }) {
  return (
    <div className="glass p-4">
      <div className="flex items-center gap-2 mb-2 opacity-60">
        <Icon size={16} />
        <span className="text-sm">{label}</span>
      </div>
      <div className={`text-2xl font-bold ${color}`}>{value}</div>
      {subValue && <div className="text-xs opacity-50">{subValue}</div>}
    </div>
  )
}

function ImageCard({ image, onReject, onApprove, onDelete, onUnpublish, onPublish, categories, onSetCategory }) {
  const [showCategorySelect, setShowCategorySelect] = useState(false)
  const publishBadges = getPublishBadges(image)
  const publishState = getPublishStateSummary(image)
  const siteSeoState = getSiteSeoState(image)

  // Format large numbers
  const formatNum = (n) => {
    if (n >= 1000000) return (n / 1000000).toFixed(1) + 'M'
    if (n >= 1000) return (n / 1000).toFixed(1) + 'K'
    return n?.toFixed?.(1) || n || 0
  }

  // Block reason labels
  const blockReasonLabels = {
    'no_category': 'Нет категории',
    'not_downloaded': 'Не скачано',
    'not_detailed': 'Нет деталей',
    'needs_review': 'На модерации',
    'rejected': 'Отклонено',
    'status_parsed': 'Не обработано',
    'status_error': 'Ошибка',
    'ready': 'Готово к публикации',
  }

  const blockReasonColors = {
    'no_category': 'bg-orange-500/80',
    'not_downloaded': 'bg-yellow-500/80',
    'not_detailed': 'bg-gray-500/80',
    'needs_review': 'bg-purple-500/80',
    'rejected': 'bg-red-500/80',
    'status_parsed': 'bg-gray-500/80',
    'status_error': 'bg-red-500/80',
    'ready': 'bg-green-500/80',
  }

  // shares is the main metric from galleru (sends)
  const shares = image.shares || 0
  const likes = image.likes || 0
  const dailyScore = image.daily_score || image.relative_score || 0
  const daysOnSource = image.days_on_source || 1
  const targets = getPublishedTargets(image)
  const canPublish = image.status === 'downloaded' || image.status === 'published'
  const canPublishAgr = canPublish && !targets.has('agr')
  const canPublishSite = canPublish && !targets.has('lojs')
  const canPublishBoth = canPublishAgr && canPublishSite

  const handleCategorySelect = (categoryId) => {
    onSetCategory(image.id, categoryId)
    setShowCategorySelect(false)
  }

  return (
    <div className="glass p-3 group">
      {/* Thumbnail */}
      <div className="aspect-square rounded-lg overflow-hidden bg-white/5 mb-3 relative">
        <img
          src={image.thumbnail_url}
          alt={image.title}
          className="w-full h-full object-cover"
          loading="lazy"
        />
        {image.lojs_public_url && (
          <a
            href={`https://legkie-otkrytki.ru${image.lojs_public_url}`}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(event) => event.stopPropagation()}
            className="absolute top-12 left-2 p-2 rounded-full bg-cyan-500/85 text-white hover:bg-cyan-500 z-10"
            title="Открыть на сайте"
          >
            <ExternalLink size={14} />
          </a>
        )}

        {/* Overlay with actions */}
        <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
          {image.page_url && (
            <a
              href={image.page_url}
              target="_blank"
              rel="noopener noreferrer"
              className="p-2 bg-white/20 rounded-full hover:bg-white/40"
              title="Открыть оригинал"
            >
              <ExternalLink size={18} />
            </a>
          )}
          {image.block_reason === 'no_category' && (
            <button
              onClick={() => setShowCategorySelect(true)}
              className="p-2 bg-orange-500/50 rounded-full hover:bg-orange-500"
              title="Назначить категорию"
            >
              <Tag size={18} />
            </button>
          )}
          {!image.is_published && image.status !== 'approved' && image.status !== 'downloaded' && (
            <button
              onClick={() => onApprove(image.id)}
              className="p-2 bg-green-500/50 rounded-full hover:bg-green-500"
              title="Одобрить в публикацию"
            >
              <Check size={18} />
            </button>
          )}
          <button
            onClick={() => onReject(image.id)}
            className="p-2 bg-red-500/50 rounded-full hover:bg-red-500"
            title="Отклонить"
          >
            <X size={18} />
          </button>
          {onUnpublish && image.is_published && (
            <button
              onClick={() => onUnpublish(image.id)}
              className="p-2 bg-yellow-500/50 rounded-full hover:bg-yellow-500"
              title="Снять с публикации"
            >
              <Undo2 size={18} />
            </button>
          )}
          {onDelete && (
            <button
              onClick={() => onDelete(image.id)}
              className="p-2 bg-red-800/50 rounded-full hover:bg-red-800"
              title="Удалить навсегда"
            >
              <Trash2 size={18} />
            </button>
          )}
        </div>

        {/* Category select dropdown */}
        {showCategorySelect && (
          <div className="absolute inset-0 bg-black/90 flex flex-col p-2 overflow-y-auto z-10">
            <div className="flex justify-between items-center mb-2">
              <span className="text-xs font-bold">Выберите категорию:</span>
              <button onClick={() => setShowCategorySelect(false)} className="text-white/60 hover:text-white">
                <X size={14} />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto space-y-1">
              {categories?.map(cat => (
                <button
                  key={cat.id}
                  onClick={() => handleCategorySelect(cat.id)}
                  className="w-full text-left px-2 py-1 text-xs bg-white/10 hover:bg-white/20 rounded truncate"
                >
                  {cat.name_ru}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Percentile badge */}
        <div className="absolute top-2 right-2 px-2 py-1 bg-black/70 rounded text-xs font-bold">
          {image.percentile_rank?.toFixed(0) || 0}%
        </div>

        {/* Daily score badge */}
        <div className="absolute top-2 left-2 px-2 py-1 bg-purple-500/80 rounded text-xs font-bold">
          {formatNum(dailyScore)}/д
        </div>

        {/* Published badges */}
        {publishBadges.length > 0 && (
          <div className="absolute bottom-2 left-2 flex flex-wrap gap-1 max-w-[80%]">
            {publishBadges.map(badge => (
              <div
                key={badge.key}
                className={`px-2 py-1 rounded text-xs font-bold ${badge.className}`}
              >
                {badge.label}
              </div>
            ))}
          </div>
        )}
        {siteSeoState && !showCategorySelect && (
          <div className="absolute bottom-2 right-2">
            <div className={`px-2 py-1 rounded text-xs font-bold ${siteSeoState.badgeClassName}`}>
              {siteSeoState.label}
            </div>
          </div>
        )}

        {/* Block reason badge - show for non-published images */}
        {image.block_reason && !showCategorySelect && (
          <div className={`absolute ${publishBadges.length > 0 ? 'bottom-10' : 'bottom-2'} left-2 px-2 py-1 ${blockReasonColors[image.block_reason] || 'bg-gray-500/80'} rounded text-xs font-bold`}>
            {blockReasonLabels[image.block_reason] || image.block_reason}
          </div>
        )}
      </div>

      {/* Stats - show shares (main metric) and likes */}
      <div className="text-xs space-y-1">
        <div className="flex justify-between items-center">
          <span className="text-green-400 font-medium">📤 {formatNum(shares)}</span>
          {likes > 0 && <span className="text-red-400">❤️ {likes}</span>}
        </div>
        <div className="flex justify-between items-center opacity-70">
          <span>📅 {daysOnSource}д на сайте</span>
          <span className="text-purple-400">⚡ {formatNum(dailyScore)}</span>
        </div>
        {image.source_added_at && (
          <div className="text-xs opacity-50">
            Добавлено: {new Date(image.source_added_at).toLocaleDateString('ru-RU')}
          </div>
        )}
        {image.author_name && (
          <div className="truncate opacity-50">{image.author_name}</div>
        )}
        {image.page_url && (
          <div className="truncate opacity-40 flex items-center gap-1">
            <Globe size={10} />
            <span>{(() => { try { return new URL(image.page_url).hostname.replace('www.', '') } catch { return '' } })()}</span>
          </div>
        )}
        {image.lojs_public_url && (
          <a
            href={`https://legkie-otkrytki.ru${image.lojs_public_url}`}
            target="_blank"
            rel="noopener noreferrer"
            className="truncate text-cyan-300 hover:text-cyan-200 flex items-center gap-1"
          >
            <ExternalLink size={10} />
            <span>Открыть на сайте</span>
          </a>
        )}
        <div className="flex items-center justify-between gap-2 pt-1">
          <span className={`font-medium ${publishState.className}`}>{publishState.label}</span>
          <span className={siteSeoState?.className || 'opacity-50'}>{siteSeoState?.label || (publishBadges.length ? publishBadges.map((badge) => badge.label).join(' + ') : 'ещё не опубликовано')}</span>
        </div>
      </div>

      {(canPublishAgr || canPublishSite) && onPublish && (
        <div className="mt-3 grid grid-cols-1 gap-2">
          {canPublishBoth && (
            <button
              onClick={() => onPublish(image.id, 'both')}
              className="flex items-center justify-center gap-2 px-3 py-2 rounded-lg bg-emerald-600/80 hover:bg-emerald-600 text-white text-xs font-semibold transition-all"
              title="Опубликовать и в AGR, и на сайт"
            >
              <Upload size={14} />
              Опубликовать везде
            </button>
          )}
          <div className="grid grid-cols-2 gap-2">
            {canPublishAgr ? (
              <button
                onClick={() => onPublish(image.id, 'agr')}
                className="flex items-center justify-center gap-2 px-3 py-2 rounded-lg bg-blue-600/80 hover:bg-blue-600 text-white text-xs font-semibold transition-all"
                title="Опубликовать в AGR"
              >
                <HardDrive size={14} />
                AGR
              </button>
            ) : (
              <div className="px-3 py-2 rounded-lg bg-white/5 text-center text-[11px] opacity-40">AGR ok</div>
            )}
            {canPublishSite ? (
              <button
                onClick={() => onPublish(image.id, 'lojs')}
                className="flex items-center justify-center gap-2 px-3 py-2 rounded-lg bg-orange-600/80 hover:bg-orange-600 text-white text-xs font-semibold transition-all"
                title="Опубликовать на сайт"
              >
                <Globe size={14} />
                SITE
              </button>
            ) : (
              <div className="px-3 py-2 rounded-lg bg-white/5 text-center text-[11px] opacity-40">Сайт ok</div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

export default function Quality() {
  const queryClient = useQueryClient()
  const { addToast } = useToast()
  const [activeTier, setActiveTier] = useState('masterpiece')
  const [publishedFilter, setPublishedFilter] = useState('new')
  const [sourceFilter, setSourceFilter] = useState(null) // null = all sources
  const [moderationMode, setModerationMode] = useState(false)
  const [moderationTab, setModerationTab] = useState('review') // 'review' | 'approved' | 'published'
  const [categoryFilter, setCategoryFilter] = useState(null) // null = all categories
  const [categorySearch, setCategorySearch] = useState('')

  const [batchProgress, setBatchProgress] = useState(null)
  const [lastBatchReport, setLastBatchReport] = useState(null)
  const [showLastBatchFailedOnly, setShowLastBatchFailedOnly] = useState(false)
  const [showPublishableOnly, setShowPublishableOnly] = useState(false)
  // Get sources for filter
  const { data: sources } = useQuery(
    'sources',
    () => sourcesAPI.getAll(),
    { staleTime: 300000 }
  )

  // Get categories for manual assignment
  const { data: categories } = useQuery(
    'categories',
    () => categoriesAPI.getAll(),
    { staleTime: 300000 }  // Cache for 5 minutes
  )

  // Get top relevant categories (holidays, weekday, everyday — by dynamic score)
  const { data: topRelevantCategories } = useQuery(
    'topRelevantCategories',
    () => categoriesAPI.getTopRelevant(),
    { staleTime: 300000 }
  )

  // Filter categories by search text
  const filteredCategories = useMemo(() => {
    if (!categorySearch.trim() || !categories) return []
    const q = categorySearch.toLowerCase()
    return categories.filter(c => c.name_ru?.toLowerCase().includes(q)).slice(0, 8)
  }, [categories, categorySearch])

  // Active category label
  const activeCategoryName = useMemo(() => {
    if (!categoryFilter || !categories) return null
    const cat = categories.find(c => c.id === categoryFilter)
    return cat?.name_ru || null
  }, [categoryFilter, categories])

  // Get tier counts
  const { data: tierCounts, isLoading: countsLoading } = useQuery(
    ['tierCounts', sourceFilter],
    () => imagesAPI.getTierCounts(sourceFilter ? { source_id: sourceFilter } : {}),
    { refetchInterval: 60000 }
  )

  // Get engagement stats
  const { data: engagementStats } = useQuery(
    'engagementStats',
    () => imagesAPI.getEngagementStats(),
    { refetchInterval: 60000 }
  )

  // Get images for active tier with filter
  const { data: tierData, isLoading: imagesLoading } = useQuery(
    ['tierImages', activeTier, publishedFilter, sourceFilter, categoryFilter],
    () => imagesAPI.getByTier(activeTier, {
      limit: 100,
      published_filter: publishedFilter,
      ...(sourceFilter ? { source_id: sourceFilter } : {}),
      ...(categoryFilter ? { agg_category_id: categoryFilter } : {})
    }),
    { keepPreviousData: true, enabled: !moderationMode }
  )

  // Get needs_review images (moderation mode)
  const { data: reviewImages, isLoading: reviewLoading } = useQuery(
    ['reviewImages', sourceFilter, categoryFilter],
    () => imagesAPI.getAll({ status: 'needs_review', limit: 100, ...(sourceFilter ? { source_id: sourceFilter } : {}), ...(categoryFilter ? { agg_category_id: categoryFilter } : {}) }),
    { enabled: moderationMode }
  )

  // Get approved images (moderation mode)
  const { data: approvedImages, isLoading: approvedLoading } = useQuery(
    ['approvedImages', sourceFilter, categoryFilter],
    () => imagesAPI.getAll({ status: 'approved', limit: 100, ...(sourceFilter ? { source_id: sourceFilter } : {}), ...(categoryFilter ? { agg_category_id: categoryFilter } : {}) }),
    { enabled: moderationMode && moderationTab === 'approved' }
  )

  // Get published images (moderation mode)
  const { data: publishedImages, isLoading: publishedLoading } = useQuery(
    ['publishedImages', sourceFilter, categoryFilter],
    () => imagesAPI.getAll({ status: 'published', limit: 100, ...(sourceFilter ? { source_id: sourceFilter } : {}), ...(categoryFilter ? { agg_category_id: categoryFilter } : {}) }),
    { enabled: moderationMode && moderationTab === 'published' }
  )

  const filteredTierImages = useMemo(
    () => filterImagesByPublishTarget(tierData?.images, publishedFilter),
    [tierData, publishedFilter]
  )

  const filteredReviewImages = useMemo(
    () => filterImagesByPublishTarget(reviewImages, publishedFilter),
    [reviewImages, publishedFilter]
  )

  const filteredApprovedImages = useMemo(
    () => filterImagesByPublishTarget(approvedImages, publishedFilter),
    [approvedImages, publishedFilter]
  )

  const filteredPublishedImages = useMemo(
    () => filterImagesByPublishTarget(publishedImages, publishedFilter),
    [publishedImages, publishedFilter]
  )

  const [catalogCategoryFilter, setCatalogCategoryFilter] = useState(null)
  const [catalogSearch, setCatalogSearch] = useState('')

  // Get catalog categories
  const { data: catalogCategories } = useQuery(
    'catalogCategories',
    () => catalogAPI.getCategories(),
    { enabled: moderationMode && moderationTab === 'catalog', staleTime: 300000 }
  )

  // Get catalog postcards (moderation mode) — реально опубликованные в каталоге
  const { data: catalogData, isLoading: catalogLoading } = useQuery(
    ['catalogPostcards', catalogCategoryFilter, catalogSearch],
    () => catalogAPI.getPostcards({
      limit: 100,
      ...(catalogCategoryFilter ? { category_id: catalogCategoryFilter } : {}),
      ...(catalogSearch ? { search: catalogSearch } : {}),
    }),
    { enabled: moderationMode && moderationTab === 'catalog' }
  )

  // Recalculate mutation
  const recalcMutation = useMutation(
    () => imagesAPI.recalculateScores(),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('tierCounts')
        queryClient.invalidateQueries('tierImages')
        queryClient.invalidateQueries('engagementStats')
      }
    }
  )

  // Reject mutation
  const rejectMutation = useMutation(
    (imageId) => imagesAPI.reject(imageId),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('tierImages')
        queryClient.invalidateQueries('tierCounts')
        queryClient.invalidateQueries('reviewImages')
        queryClient.invalidateQueries('approvedImages')
        queryClient.invalidateQueries('publishedImages')
      }
    }
  )

  // Approve mutation
  const approveMutation = useMutation(
    (imageId) => imagesAPI.approve(imageId),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('tierImages')
        queryClient.invalidateQueries('tierCounts')
        queryClient.invalidateQueries('reviewImages')
        queryClient.invalidateQueries('approvedImages')
        queryClient.invalidateQueries('publishedImages')
      }
    }
  )

  // Delete mutation
  const deleteMutation = useMutation(
    (imageId) => imagesAPI.delete(imageId),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('reviewImages')
        queryClient.invalidateQueries('approvedImages')
        queryClient.invalidateQueries('tierImages')
        queryClient.invalidateQueries('tierCounts')
      }
    }
  )

  // Set category mutation
  const setCategoryMutation = useMutation(
    ({ imageId, categoryId }) => imagesAPI.setCategory(imageId, categoryId),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('tierImages')
        queryClient.invalidateQueries('tierCounts')
      }
    }
  )

  // Unpublish mutation
  const unpublishMutation = useMutation(
    (imageId) => imagesAPI.unpublish(imageId),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('tierImages')
        queryClient.invalidateQueries('tierCounts')
        queryClient.invalidateQueries('reviewImages')
        queryClient.invalidateQueries('approvedImages')
        queryClient.invalidateQueries('publishedImages')
      }
    }
  )

  // Publish mutation
  const publishMutation = useMutation(
    ({ imageId, target }) => imagesAPI.publish(imageId, target),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('tierImages')
        queryClient.invalidateQueries('tierCounts')
        queryClient.invalidateQueries('reviewImages')
        queryClient.invalidateQueries('approvedImages')
        queryClient.invalidateQueries('publishedImages')
        queryClient.invalidateQueries('images')
        queryClient.invalidateQueries('imageStats')
        queryClient.invalidateQueries('imageTierCountsForImagesPage')
      }
    }
  )

  const batchPublishMutation = useMutation(
    async ({ imageIds, target }) => {
      const results = []
      setBatchProgress({ total: imageIds.length, done: 0, failed: 0, target })
      setLastBatchReport(null)

      for (const imageId of imageIds) {
        try {
          const result = await imagesAPI.publish(imageId, target)
          results.push({ ok: true, imageId, result })
          setBatchProgress(prev => prev ? { ...prev, done: prev.done + 1 } : prev)
        } catch (error) {
          results.push({
            ok: false,
            imageId,
            message: error?.response?.data?.detail || error?.message || 'Неизвестная ошибка',
          })
          setBatchProgress(prev => prev ? { ...prev, done: prev.done + 1, failed: prev.failed + 1 } : prev)
        }
      }

      return results
    },
    {
      onSuccess: (results, variables) => {
        queryClient.invalidateQueries('tierImages')
        queryClient.invalidateQueries('tierCounts')
        queryClient.invalidateQueries('reviewImages')
        queryClient.invalidateQueries('approvedImages')
        queryClient.invalidateQueries('publishedImages')
        queryClient.invalidateQueries('images')
        queryClient.invalidateQueries('imageStats')
        queryClient.invalidateQueries('imageTierCountsForImagesPage')

        const failedItems = results.filter(item => !item.ok)
        const failed = failedItems.length
        const success = results.length - failed
        const targetLabel = variables?.target === 'lojs' ? 'SITE' : 'AGR + SITE'

        setLastBatchReport({
          targetKey: variables?.target,
          target: targetLabel,
          total: results.length,
          success,
          failed,
          failedImageIds: failedItems.map(item => item.imageId),
          failedItems: failedItems.slice(0, 10),
          finishedAt: new Date().toISOString(),
        })

        if (failed > 0) {
          addToast(`Batch publish в ${targetLabel}: ${success} ok, ${failed} ошибок`, 'error')
        } else {
          addToast(`Batch publish в ${targetLabel}: ${success} карточек опубликовано`)
        }
        setBatchProgress(null)
      },
      onError: (error) => {
        addToast(error?.response?.data?.detail || error?.message || 'Ошибка batch publish', 'error')
        setBatchProgress(null)
      }
    }
  )
  // Delete from catalog mutation
  const catalogDeleteMutation = useMutation(
    (postcardId) => catalogAPI.deletePostcard(postcardId),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('catalogPostcards')
      }
    }
  )

  const handleBatchPublish = (target) => {
    const imageIds = currentScopePublishable
      .filter(image => {
        if (target === 'lojs') return !isPublishedToSite(image)
        if (target === 'both') return !isPublishedToAgr(image) || !isPublishedToSite(image)
        return false
      })
      .map(image => image.id)

    if (!imageIds.length) {
      return
    }

    const targetLabel = target === 'lojs' ? 'SITE' : 'AGR + SITE'
    if (confirm(`Опубликовать ${imageIds.length} открыток в ${targetLabel} из текущего среза?`)) {
      batchPublishMutation.mutate({ imageIds, target })
    }
  }

  const handleRetryBatchFailed = () => {
    if (!lastBatchReport?.failedImageIds?.length || !lastBatchReport?.targetKey) {
      addToast('Нет элементов для повторной публикации', 'error')
      return
    }

    const targetLabel = lastBatchReport.target
    if (confirm(`Повторить публикацию ${lastBatchReport.failedImageIds.length} ошибок в ${targetLabel}?`)) {
      batchPublishMutation.mutate({ imageIds: lastBatchReport.failedImageIds, target: lastBatchReport.targetKey })
    }
  }

  const handleRetrySingleFailed = (imageId) => {
    if (!lastBatchReport?.targetKey) {
      addToast('Не удалось определить target последнего batch', 'error')
      return
    }

    batchPublishMutation.mutate({ imageIds: [imageId], target: lastBatchReport.targetKey })
  }

  const handleReject = (imageId) => {
    if (confirm('Отклонить эту открытку?')) {
      rejectMutation.mutate(imageId)
    }
  }

  const handleApprove = (imageId) => {
    approveMutation.mutate(imageId)
  }

  const handleUnpublish = (imageId) => {
    if (confirm('Снять с публикации?')) {
      unpublishMutation.mutate(imageId)
    }
  }

  const handlePublish = (imageId, target) => {
    publishMutation.mutate({ imageId, target })
  }

  const handleCatalogDelete = (postcardId) => {
    if (confirm('Удалить эту открытку из каталога?')) {
      catalogDeleteMutation.mutate(postcardId)
    }
  }

  const handleDelete = (imageId) => {
    if (confirm('Удалить эту открытку навсегда?')) {
      deleteMutation.mutate(imageId)
    }
  }

  const handleSetCategory = (imageId, categoryId) => {
    setCategoryMutation.mutate({ imageId, categoryId })
  }

  const currentTier = TIERS[activeTier]
  const TierIcon = currentTier.icon
  const publishTargetsStats = tierCounts?.targets || {}
  const currentScopeImages = useMemo(() => {
    if (moderationMode) {
      if (moderationTab === 'review') return filteredReviewImages || []
      if (moderationTab === 'approved') return filteredApprovedImages || []
      if (moderationTab === 'published') return filteredPublishedImages || []
      return []
    }

    return filteredTierImages || []
  }, [
    moderationMode,
    moderationTab,
    filteredReviewImages,
    filteredApprovedImages,
    filteredPublishedImages,
    filteredTierImages,
  ])

  const currentScopePublishable = useMemo(() => {
    return currentScopeImages.filter(image => image?.status === 'downloaded' || image?.status === 'published')
  }, [currentScopeImages])

  const currentScopeBatchCounts = useMemo(() => ({
    site: currentScopePublishable.filter(image => !isPublishedToSite(image)).length,
    both: currentScopePublishable.filter(image => !isPublishedToAgr(image) || !isPublishedToSite(image)).length,
  }), [currentScopePublishable])

  const currentScopeTargetCounts = useMemo(() => ({
    total: currentScopeImages.length,
    new: countByPublishTarget(currentScopeImages, 'new'),
    agr: countByPublishTarget(currentScopeImages, 'agr'),
    site: countByPublishTarget(currentScopeImages, 'site'),
    both: countByPublishTarget(currentScopeImages, 'both'),
  }), [currentScopeImages])

  const lastBatchFailedIds = useMemo(() => lastBatchReport?.failedImageIds || [], [lastBatchReport])

  const displayedCurrentScopeImages = useMemo(() => {
    let images = currentScopeImages

    if (showLastBatchFailedOnly && lastBatchFailedIds.length) {
      const failedSet = new Set(lastBatchFailedIds)
      images = images.filter(image => failedSet.has(image.id))
    }

    if (showPublishableOnly) {
      images = images.filter(image => image?.status === 'downloaded' || image?.status === 'published')
    }

    return images
  }, [currentScopeImages, showLastBatchFailedOnly, lastBatchFailedIds, showPublishableOnly])

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold">Качество и модерация</h1>

        <div className="flex gap-2">
          <button
            onClick={() => setModerationMode(!moderationMode)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-all ${
              moderationMode ? 'bg-purple-600 text-white' : 'bg-white/10 hover:bg-white/20'
            }`}
          >
            {moderationMode ? <BarChart3 size={18} /> : <Eye size={18} />}
            {moderationMode ? 'Качество' : 'Модерация'}{!moderationMode && reviewImages?.length > 0 ? ` (${reviewImages.length})` : ''}
          </button>
          <button
            onClick={() => recalcMutation.mutate()}
            disabled={recalcMutation.isLoading}
            className="btn-primary flex items-center gap-2"
          >
            <RefreshCw size={18} className={recalcMutation.isLoading ? 'animate-spin' : ''} />
          Пересчитать
          </button>
        </div>
      </div>

      {/* Source filter */}
      {sources?.length > 0 && (
        <div className="flex gap-2 mb-4 flex-wrap items-center">
          <Filter size={16} className="opacity-50" />
          <button
            onClick={() => setSourceFilter(null)}
            className={`px-3 py-1.5 rounded-lg text-sm transition-all ${
              sourceFilter === null
                ? 'bg-primary text-white'
                : 'bg-white/10 hover:bg-white/20'
            }`}
          >
            Все источники
          </button>
          {sources.map(src => (
            <button
              key={src.id}
              onClick={() => setSourceFilter(src.id)}
              className={`px-3 py-1.5 rounded-lg text-sm transition-all ${
                sourceFilter === src.id
                  ? 'bg-primary text-white'
                  : 'bg-white/10 hover:bg-white/20'
              }`}
            >
              {src.name}
            </button>
          ))}
        </div>
      )}

      {/* Category filter */}
      <div className="mb-4">
        <div className="flex gap-2 items-center mb-2">
          <Tag size={16} className="opacity-50" />
          <div className="relative flex-1 max-w-sm">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 opacity-40" />
            <input
              type="text"
              placeholder="Поиск категории..."
              value={categorySearch}
              onChange={(e) => setCategorySearch(e.target.value)}
              className="w-full pl-8 pr-3 py-1.5 rounded-lg text-sm bg-white/10 border border-white/10 focus:border-primary focus:outline-none placeholder:opacity-40"
            />
          </div>
          {categoryFilter && (
            <button
              onClick={() => { setCategoryFilter(null); setCategorySearch('') }}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm bg-primary text-white"
            >
              {activeCategoryName}
              <X size={14} />
            </button>
          )}
        </div>

        {/* Search results dropdown */}
        {categorySearch.trim() && filteredCategories.length > 0 && (
          <div className="flex gap-1.5 flex-wrap mb-2">
            {filteredCategories.map(cat => (
              <button
                key={cat.id}
                onClick={() => { setCategoryFilter(cat.id); setCategorySearch('') }}
                className={`px-3 py-1 rounded-lg text-xs transition-all ${
                  categoryFilter === cat.id
                    ? 'bg-primary text-white'
                    : 'bg-white/10 hover:bg-white/20'
                }`}
              >
                {cat.name_ru}
              </button>
            ))}
          </div>
        )}

        {/* Top relevant quick buttons */}
        {!categorySearch.trim() && topRelevantCategories?.length > 0 && (
          <div className="flex gap-1.5 flex-wrap">
            {topRelevantCategories.filter(c => c.dynamic_score > 50).slice(0, 5).map(cat => {
              const typeEmoji = { holiday: '🎉', weekday: '📅', everyday: '☀️', seasonal: '🍃' }
              return (
                <button
                  key={cat.id}
                  onClick={() => setCategoryFilter(categoryFilter === cat.id ? null : cat.id)}
                  className={`flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs transition-all ${
                    categoryFilter === cat.id
                      ? 'bg-primary text-white ring-2 ring-primary/50'
                      : cat.dynamic_score >= 800
                        ? 'bg-yellow-500/20 hover:bg-yellow-500/30 text-yellow-300'
                        : cat.dynamic_score >= 400
                          ? 'bg-orange-500/15 hover:bg-orange-500/25 text-orange-300'
                          : 'bg-white/10 hover:bg-white/20'
                  }`}
                >
                  <span>{typeEmoji[cat.category_type] || '📂'}</span>
                  <span>{cat.name_ru}</span>
                  {cat.images_count > 0 && <span className="opacity-50">({cat.images_count})</span>}
                </button>
              )
            })}
          </div>
        )}
      </div>

      {moderationMode ? (
        <>
          {/* Publish target filter */}
          <div className="flex gap-2 mb-4 flex-wrap">
            {PUBLISH_TARGET_FILTERS.map(({ key, label, color }) => {
              const currentImages =
                moderationTab === 'review' ? reviewImages :
                moderationTab === 'approved' ? approvedImages :
                moderationTab === 'published' ? publishedImages :

                []

              const count = moderationTab === 'catalog'
                ? null
                : countByPublishTarget(currentImages, key)

              return (
                <button
                  key={key}
                  onClick={() => setPublishedFilter(key)}
                  className={`px-4 py-2 rounded-lg transition-all ${
                    publishedFilter === key
                      ? 'bg-primary text-white'
                      : 'bg-white/10 hover:bg-white/20'
                  } ${color}`}
                >
                  {label}
                  {count > 0 && (
                    <span className="ml-2 px-1.5 py-0.5 bg-white/10 rounded text-xs">
                      {count}
                    </span>
                  )}
                </button>
              )
            })}
          </div>
          {moderationTab !== 'catalog' && (
            <div className="flex flex-wrap gap-2 mb-4">
              <button
                onClick={() => setShowPublishableOnly(value => !value)}
                className={`px-4 py-2 rounded-lg text-sm font-semibold transition-all ${showPublishableOnly ? 'bg-cyan-500/80 text-black hover:bg-cyan-500' : 'bg-white/10 hover:bg-white/20 text-white'}`}
              >
                {showPublishableOnly ? `Только publishable: ON (${displayedCurrentScopeImages.length})` : `Только publishable (${currentScopePublishable.length})`}
              </button>
              <button
                onClick={() => handleBatchPublish('lojs')}
                disabled={batchPublishMutation.isLoading || currentScopeBatchCounts.site === 0}
                className="flex items-center gap-2 px-4 py-2 rounded-lg bg-orange-600/80 hover:bg-orange-600 disabled:opacity-40 disabled:cursor-not-allowed text-white text-sm font-semibold transition-all"
              >
                <Globe size={16} />
                На SITE видимый хвост ({currentScopeBatchCounts.site})
              </button>
              <button
                onClick={() => handleBatchPublish('both')}
                disabled={batchPublishMutation.isLoading || currentScopeBatchCounts.both === 0}
                className="flex items-center gap-2 px-4 py-2 rounded-lg bg-emerald-600/80 hover:bg-emerald-600 disabled:opacity-40 disabled:cursor-not-allowed text-white text-sm font-semibold transition-all"
              >
                <Upload size={16} />
                В AGR + SITE ({currentScopeBatchCounts.both})
              </button>
            </div>
          )}

          {batchProgress && (
            <div className="glass p-3 mb-4 border border-white/10">
              <div className="flex flex-wrap items-center gap-3 text-sm">
                <span className="font-semibold">Batch publish:</span>
                <span>{batchProgress.target === 'lojs' ? 'SITE' : 'AGR + SITE'}</span>
                <span className="opacity-70">{batchProgress.done} / {batchProgress.total}</span>
                <span className="text-red-300">Ошибок: {batchProgress.failed}</span>
              </div>
              <div className="mt-2 h-2 rounded bg-white/10 overflow-hidden">
                <div
                  className="h-full bg-primary transition-all"
                  style={{ width: `${batchProgress.total ? (batchProgress.done / batchProgress.total) * 100 : 0}%` }}
                />
              </div>
            </div>
          )}

          {lastBatchReport && (
            <div className="glass p-3 mb-4 border border-white/10">
              <div className="flex flex-wrap items-center gap-3 text-sm mb-2">
                <span className="font-semibold">Последний batch:</span>
                <span>{lastBatchReport.target}</span>
                <span className="text-green-300">Успех: {lastBatchReport.success}</span>
                <span className="text-red-300">Ошибки: {lastBatchReport.failed}</span>
                <span className="opacity-60">Всего: {lastBatchReport.total}</span>
                {lastBatchReport.failed > 0 && (
                  <>
                    <button
                      onClick={() => setShowLastBatchFailedOnly(value => !value)}
                      className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${showLastBatchFailedOnly ? 'bg-yellow-500/80 text-black hover:bg-yellow-500' : 'bg-white/10 hover:bg-white/20 text-white'}`}
                    >
                      {showLastBatchFailedOnly ? 'Показать весь срез' : `Только failed в сетке (${lastBatchReport.failed})`}
                    </button>
                    <button
                      onClick={handleRetryBatchFailed}
                      disabled={batchPublishMutation.isLoading}
                      className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-red-600/80 hover:bg-red-600 disabled:opacity-40 disabled:cursor-not-allowed text-white text-xs font-semibold transition-all"
                    >
                      <RefreshCw size={14} />
                      Повторить ошибки ({lastBatchReport.failed})
                    </button>
                  </>
                )}
              </div>
              {showLastBatchFailedOnly && lastBatchFailedIds.length > 0 && (
                <div className="mb-2 text-xs text-yellow-200">
                  В сетке показаны только карточки с ошибкой из последнего батча: {displayedCurrentScopeImages.length} из {lastBatchFailedIds.length}
                </div>
              )}
              {lastBatchReport.failedItems?.length > 0 && (
                <div className="space-y-1 text-xs">
                  {lastBatchReport.failedItems.map(item => (
                    <div key={item.imageId} className="flex flex-wrap gap-2 px-2 py-1 rounded bg-red-500/10 border border-red-500/20">
                      <span className="font-mono text-red-200">image #{item.imageId}</span>
                      <span className="opacity-80">{item.message}</span>
                      <button
                        onClick={() => handleRetrySingleFailed(item.imageId)}
                        disabled={batchPublishMutation.isLoading}
                        className="ml-auto px-2 py-0.5 rounded bg-white/10 hover:bg-white/20 disabled:opacity-40 disabled:cursor-not-allowed text-[11px] font-semibold transition-all"
                      >
                        Retry
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {moderationTab !== 'catalog' && (
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4">
              <div className="glass p-3">
                <div className="text-xs opacity-60 mb-1">Текущий срез</div>
                <div className="text-xl font-bold">{currentScopeTargetCounts.total}</div>
              </div>
              <div className="glass p-3">
                <div className="text-xs opacity-60 mb-1">Новые</div>
                <div className="text-xl font-bold text-green-400">{currentScopeTargetCounts.new}</div>
              </div>
              <div className="glass p-3">
                <div className="text-xs opacity-60 mb-1">AGR</div>
                <div className="text-xl font-bold text-blue-300">{currentScopeTargetCounts.agr}</div>
              </div>
              <div className="glass p-3">
                <div className="text-xs opacity-60 mb-1">SITE</div>
                <div className="text-xl font-bold text-orange-300">{currentScopeTargetCounts.site}</div>
              </div>
              <div className="glass p-3">
                <div className="text-xs opacity-60 mb-1">AGR + SITE</div>
                <div className="text-xl font-bold text-purple-300">{currentScopeTargetCounts.both}</div>
              </div>
            </div>
          )}

          {/* Sub-tabs */}
          <div className="flex gap-2 mb-4">
            <button
              onClick={() => setModerationTab('review')}
              className={`px-4 py-2 rounded-lg transition-all ${
                moderationTab === 'review' ? 'bg-purple-600 text-white' : 'bg-white/10 hover:bg-white/20'
              }`}
            >
              На проверке{reviewImages?.length > 0 ? ` (${reviewImages.length})` : ''}
            </button>
            <button
              onClick={() => setModerationTab('approved')}
              className={`px-4 py-2 rounded-lg transition-all ${
                moderationTab === 'approved' ? 'bg-green-600 text-white' : 'bg-white/10 hover:bg-white/20'
              }`}
            >
              Одобренные{approvedImages?.length > 0 ? ` (${approvedImages.length})` : ''}
            </button>
            <button
              onClick={() => setModerationTab('published')}
              className={`px-4 py-2 rounded-lg transition-all ${
                moderationTab === 'published' ? 'bg-blue-600 text-white' : 'bg-white/10 hover:bg-white/20'
              }`}
            >
              Опубликованные{publishedImages?.length > 0 ? ` (${publishedImages.length})` : ''}
            </button>
            <button
              onClick={() => setModerationTab('catalog')}
              className={`px-4 py-2 rounded-lg transition-all ${
                moderationTab === 'catalog' ? 'bg-emerald-600 text-white' : 'bg-white/10 hover:bg-white/20'
              }`}
            >
              В каталоге{catalogData?.total ? ` (${catalogData.total})` : ''}
            </button>
          </div>

          {moderationTab === 'review' ? (
            <>
              {reviewLoading ? (
                <div className="text-center py-12 opacity-50">Загрузка...</div>
              ) : !displayedCurrentScopeImages.length ? (
                <div className="text-center py-12 opacity-50">Нет открыток на модерации.</div>
              ) : (
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4">
                  {displayedCurrentScopeImages.map(image => (
                    <ImageCard
                      key={image.id}
                      image={image}
                      onReject={handleReject}
                      onApprove={handleApprove}
                      onUnpublish={handleUnpublish}
                      onPublish={handlePublish}
                      categories={categories}
                      onSetCategory={handleSetCategory}
                    />
                  ))}
                </div>
              )}
            </>
          ) : moderationTab === 'approved' ? (
            <>
              {approvedLoading ? (
                <div className="text-center py-12 opacity-50">Загрузка...</div>
              ) : !displayedCurrentScopeImages.length ? (
                <div className="text-center py-12 opacity-50">Нет одобренных открыток.</div>
              ) : (
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4">
                  {displayedCurrentScopeImages.map(image => (
                    <ImageCard
                      key={image.id}
                      image={image}
                      onReject={handleReject}
                      onApprove={handleApprove}
                      onDelete={handleDelete}
                      onUnpublish={handleUnpublish}
                      onPublish={handlePublish}
                      categories={categories}
                      onSetCategory={handleSetCategory}
                    />
                  ))}
                </div>
              )}
            </>
          ) : moderationTab === 'published' ? (
            <>
              {publishedLoading ? (
                <div className="text-center py-12 opacity-50">Загрузка...</div>
              ) : !displayedCurrentScopeImages.length ? (
                <div className="text-center py-12 opacity-50">Нет опубликованных открыток.</div>
              ) : (
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4">
                  {displayedCurrentScopeImages.map(image => (
                    <ImageCard
                      key={image.id}
                      image={image}
                      onReject={handleReject}
                      onUnpublish={handleUnpublish}
                      onPublish={handlePublish}
                      categories={categories}
                      onSetCategory={handleSetCategory}
                    />
                  ))}
                </div>
              )}
            </>
          ) : moderationTab === 'catalog' ? (
            <>
              {/* Catalog category filter */}
              <div className="mb-4 space-y-2">
                <div className="flex gap-2 items-center">
                  <Search size={14} className="opacity-40" />
                  <input
                    type="text"
                    placeholder="Поиск по названию..."
                    value={catalogSearch}
                    onChange={(e) => setCatalogSearch(e.target.value)}
                    className="flex-1 max-w-sm px-3 py-1.5 rounded-lg text-sm bg-white/10 border border-white/10 focus:border-primary focus:outline-none placeholder:opacity-40"
                  />
                  {catalogCategoryFilter && (
                    <button
                      onClick={() => setCatalogCategoryFilter(null)}
                      className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm bg-emerald-600 text-white"
                    >
                      {catalogCategories?.find(c => c.id === catalogCategoryFilter)?.name || 'Категория'}
                      <X size={14} />
                    </button>
                  )}
                  {catalogData?.total > 0 && (
                    <span className="text-sm opacity-50">Найдено: {catalogData.total}</span>
                  )}
                </div>
                {catalogCategories?.length > 0 && (
                  <div className="flex gap-1.5 flex-wrap">
                    {catalogCategories.slice(0, 20).map(cat => (
                      <button
                        key={cat.id}
                        onClick={() => setCatalogCategoryFilter(catalogCategoryFilter === cat.id ? null : cat.id)}
                        className={`px-2.5 py-1 rounded-lg text-xs transition-all ${
                          catalogCategoryFilter === cat.id
                            ? 'bg-emerald-600 text-white ring-2 ring-emerald-500/50'
                            : 'bg-white/10 hover:bg-white/20'
                        }`}
                      >
                        {cat.name} <span className="opacity-50">({cat.count})</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {catalogLoading ? (
                <div className="text-center py-12 opacity-50">Загрузка...</div>
              ) : !catalogData?.postcards?.length ? (
                <div className="text-center py-12 opacity-50">Нет открыток в каталоге.</div>
              ) : (
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4">
                  {catalogData.postcards.map(pc => (
                    <div key={pc.id} className="glass p-3 group">
                      <div className="aspect-square rounded-lg overflow-hidden bg-white/5 mb-3 relative">
                        {pc.image_url?.toLowerCase().endsWith('.mp4') || pc.image_url?.toLowerCase().endsWith('.webm') ? (
                          <video src={pc.image_url} className="w-full h-full object-cover" muted loop autoPlay playsInline />
                        ) : (
                          <img src={pc.image_url} alt={pc.title || ''} className="w-full h-full object-cover" loading="lazy" />
                        )}
                        <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                          <button
                            onClick={() => handleCatalogDelete(pc.id)}
                            className="p-2 bg-red-600/70 rounded-full hover:bg-red-600"
                            title="Удалить из каталога"
                          >
                            <Trash2 size={18} />
                          </button>
                          {pc.image_url && (
                            <a href={pc.image_url} target="_blank" rel="noopener noreferrer"
                              className="p-2 bg-white/20 rounded-full hover:bg-white/40" title="Открыть оригинал">
                              <ExternalLink size={18} />
                            </a>
                          )}
                        </div>
                        <div className="absolute top-2 left-2 px-2 py-1 bg-emerald-500/80 rounded text-xs font-bold">
                          #{pc.id}
                        </div>
                        {pc.category_name && (
                          <div className="absolute bottom-2 left-2 px-2 py-1 bg-blue-500/80 rounded text-xs font-bold truncate max-w-[80%]">
                            {pc.category_name}
                          </div>
                        )}
                      </div>
                      <div className="text-xs space-y-1">
                        {pc.title && <div className="truncate opacity-70">{pc.title}</div>}
                        <div className="flex justify-between items-center opacity-50">
                          <span>{pc.created_at ? new Date(pc.created_at).toLocaleDateString('ru-RU') : ''}</span>
                          {pc.likes_count > 0 && <span className="text-red-400">❤️ {pc.likes_count}</span>}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </>
          ) : null}
        </>
      ) : (
        /* Tier-based quality view */
        <>
      {/* Site-wide stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3 mb-6">
        <StatCard
          icon={FileText}
          label="Распарсено"
          value={engagementStats?.total_parsed || 0}
          subValue="открыток найдено"
        />
        <StatCard
          icon={Share2}
          label="Всего шарингов"
          value={(engagementStats?.total_shares || 0).toLocaleString()}
          subValue="на сайте-источнике"
          color="text-green-400"
        />
        <StatCard
          icon={Heart}
          label="Всего лайков"
          value={engagementStats?.total_likes || 0}
          subValue="на сайте-источнике"
          color="text-red-400"
        />
        <StatCard
          icon={BarChart3}
          label="Ср. score/день"
          value={(engagementStats?.avg_daily_score || 0).toFixed(1)}
          subValue="среднесуточный балл"
          color="text-purple-400"
        />
        <StatCard
          icon={TrendingUp}
          label="Макс score"
          value={(engagementStats?.max_daily_score || 0).toFixed(0)}
          subValue="лучший суточный"
          color="text-yellow-400"
        />
        <StatCard
          icon={Minus}
          label="Мин score"
          value={(engagementStats?.min_daily_score || 0).toFixed(1)}
          subValue="худший суточный"
          color="text-gray-400"
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-6">
        <StatCard
          icon={HardDrive}
          label="Только AGR"
          value={publishTargetsStats.agr_only || 0}
          subValue="Опубликованы только в приложение"
          color="text-blue-300"
        />
        <StatCard
          icon={Globe}
          label="Только сайт"
          value={publishTargetsStats.site_only || 0}
          subValue="Опубликованы только на сайт"
          color="text-orange-300"
        />
        <StatCard
          icon={CheckCircle}
          label="AGR + SITE"
          value={publishTargetsStats.both || 0}
          subValue="Опубликованы и в приложение, и на сайт"
          color="text-purple-300"
        />
      </div>

      {/* Engagement stats (only if published) */}
      {(engagementStats?.total_published > 0) && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
          <StatCard
            icon={Eye}
            label="Просмотры/день"
            value={engagementStats?.site_avg_daily_views?.toFixed(1) || '0'}
            subValue="среднее на открытку"
            color="text-blue-400"
          />
          <StatCard
            icon={Download}
            label="Скачивания/день"
            value={engagementStats?.site_avg_daily_downloads?.toFixed(2) || '0'}
            subValue="среднее на открытку"
            color="text-green-400"
          />
          <StatCard
            icon={Heart}
            label="Лайки/день"
            value={engagementStats?.site_avg_daily_likes?.toFixed(2) || '0'}
            subValue="среднее на открытку"
            color="text-red-400"
          />
          <StatCard
            icon={Share2}
            label="Шаринги/день"
            value={engagementStats?.site_avg_daily_shares?.toFixed(2) || '0'}
            subValue="среднее на открытку"
            color="text-purple-400"
          />
        </div>
      )}

      {/* Info box when no parsed images at all */}
      {(!engagementStats?.total_parsed || engagementStats.total_parsed === 0) && (
        <div className="glass p-6 mb-6 text-center">
          <BarChart3 size={48} className="mx-auto mb-4 opacity-30" />
          <h3 className="text-lg font-semibold mb-2">Нет данных для анализа</h3>
          <p className="text-sm opacity-60 mb-4">
            Сначала нужно распарсить открытки. Перейдите на страницу Queue и запустите парсинг.
          </p>
        </div>
      )}

      {/* Info box when parsed but not published */}
      {(engagementStats?.total_parsed > 0 && engagementStats?.total_published === 0) && (
        <div className="glass p-6 mb-6 border-l-4 border-yellow-500">
          <h3 className="text-lg font-semibold mb-2">📊 Пайплайн обработки</h3>
          <p className="text-sm opacity-70 mb-4">
            Качество определяется по статистике с сайта-источника (лайки, шаринги).
            Топовые открытки — те, что популярны на источнике.
          </p>
          <div className="grid grid-cols-4 gap-2 text-center text-sm">
            <div className={`p-2 rounded ${engagementStats.total_parsed > 0 ? 'bg-green-500/20' : 'bg-white/5'}`}>
              <div className="font-bold">{engagementStats.total_parsed}</div>
              <div className="opacity-60 text-xs">Распарсено</div>
            </div>
            <div className={`p-2 rounded ${engagementStats.total_approved > 0 ? 'bg-green-500/20' : 'bg-white/5'}`}>
              <div className="font-bold">{engagementStats.total_approved}</div>
              <div className="opacity-60 text-xs">Одобрено</div>
            </div>
            <div className={`p-2 rounded ${engagementStats.total_downloaded > 0 ? 'bg-green-500/20' : 'bg-white/5'}`}>
              <div className="font-bold">{engagementStats.total_downloaded}</div>
              <div className="opacity-60 text-xs">Скачано</div>
            </div>
            <div className={`p-2 rounded ${engagementStats.total_published > 0 ? 'bg-green-500/20' : 'bg-white/5'}`}>
              <div className="font-bold">{engagementStats.total_published}</div>
              <div className="opacity-60 text-xs">Опубликовано</div>
            </div>
          </div>
        </div>
      )}

      {/* Tier tabs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        {Object.entries(TIERS).map(([key, tier]) => {
          const Icon = tier.icon
          const count = tierCounts?.[key] || 0
          const newCount = tierCounts?.new?.[key] || 0
          const publishedCount = tierCounts?.published?.[key] || 0
          const isActive = activeTier === key

          return (
            <button
              key={key}
              onClick={() => setActiveTier(key)}
              className={`glass p-4 text-left transition-all ${
                isActive ? 'ring-2 ring-primary' : 'hover:bg-white/5'
              }`}
            >
              <div className="flex items-center gap-2 mb-2">
                <Icon size={20} className={tier.color} />
                <span className="font-semibold">{tier.label}</span>
              </div>
              <div className="text-2xl font-bold">{count}</div>
              <div className="flex gap-2 text-xs mt-1">
                <span className="text-green-400">{newCount} новых</span>
                <span className="text-blue-400">{publishedCount} опубл.</span>
              </div>
              <div className="text-xs opacity-50">{tier.range}</div>
            </button>
          )
        })}
      </div>

      {/* Published filter tabs */}
      <div className="flex gap-2 mb-4 flex-wrap">
        {PUBLISH_TARGET_FILTERS.map(({ key, label, color }) => (
          <button
            key={key}
            onClick={() => setPublishedFilter(key)}
            className={`px-4 py-2 rounded-lg transition-all ${
              publishedFilter === key
                ? 'bg-primary text-white'
                : 'bg-white/10 hover:bg-white/20'
            } ${color}`}
          >
            {label}
            {key === 'new' && countByPublishTarget(tierData?.images, 'new') > 0 && (
              <span className="ml-2 px-1.5 py-0.5 bg-green-500/30 rounded text-xs">
                {countByPublishTarget(tierData?.images, 'new')}
              </span>
            )}
            {key !== 'new' && key !== 'all' && countByPublishTarget(tierData?.images, key) > 0 && (
              <span className="ml-2 px-1.5 py-0.5 bg-white/10 rounded text-xs">
                {countByPublishTarget(tierData?.images, key)}
              </span>
            )}
          </button>
        ))}
      </div>

      <div className="flex gap-2 mb-4 flex-wrap">
        <button
          onClick={() => setShowPublishableOnly(value => !value)}
          className={`px-4 py-2 rounded-lg text-sm font-semibold transition-all ${showPublishableOnly ? 'bg-cyan-500/80 text-black hover:bg-cyan-500' : 'bg-white/10 hover:bg-white/20 text-white'}`}
        >
          {showPublishableOnly ? `Только publishable: ON (${displayedCurrentScopeImages.length})` : `Только publishable (${currentScopePublishable.length})`}
        </button>
      </div>

      {/* Current tier header */}
      <div className={`${currentTier.bgColor} rounded-lg p-4 mb-6 flex items-center gap-3`}>
        <TierIcon size={32} className={currentTier.color} />
        <div>
          <h2 className="text-xl font-bold">{currentTier.label}</h2>
          <p className="text-sm opacity-70">{currentTier.description}</p>
        </div>
        <div className="ml-auto text-3xl font-bold">
          {filteredTierImages.length}
        </div>
      </div>

      {/* Images grid */}
      {imagesLoading ? (
        <div className="text-center py-12 opacity-50">Загрузка...</div>
      ) : !displayedCurrentScopeImages.length ? (
        <div className="text-center py-12 opacity-50">
          Нет открыток в этой категории.
          <br />
          <span className="text-sm">Запустите пересчёт после накопления статистики.</span>
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4">
          {displayedCurrentScopeImages.map(image => (
            <ImageCard
              key={image.id}
              image={image}
              onReject={handleReject}
              onApprove={handleApprove}
              onUnpublish={handleUnpublish}
              categories={categories}
              onSetCategory={handleSetCategory}
            />
          ))}
        </div>
      )}
        </>
      )}
    </div>
  )
}










